<template>
  <div>
    <LoadingContainer v-if="loading"/>

    <div v-else class="container">
    <div class="row">
      <div class="col-xs-6">
        <HelpText>
          <h3>
            Create Portal Company
          </h3>

          <p>
            Enter the details of the portal company.
          </p>
        </HelpText>
      </div>

      <div class="col-xs-18">
        <form @submit.prevent="submit" class="form-horizontal">
          <div class="panel panel-default">
            <div class="panel-body">
              <SelectFormGroup
                  v-model="data.portal_id"
                  name="portal"
                  label="Portal"
                  :options="portalOptions"
                  :error="(errors.portal_id || [])[0]"
              />

              <TextFormGroup
                  v-model="data.legacy_reference"
                  name="legacy_reference"
                  label="Legacy Reference"
                  :error="(errors.legacy_reference || [])[0]"
              />

              <TextFormGroup
                  v-model="data.name"
                  name="name"
                  label="Name"
                  :error="(errors.name || [])[0]"
              />

              <CkEditorFormGroup
                  v-model="data.description"
                  name="description"
                  label="Description"
                  :error="(errors.description || [])[0]"
                  :toolbar="['bold', 'italic', 'link']"
              />

              <TextFormGroup
                  v-model="data.email"
                  name="email"
                  label="Email"
                  :error="(errors.email || [])[0]"
              />

              <TextFormGroup
                  v-model="data.phone"
                  name="phone"
                  label="Phone"
                  :error="(errors.phone || [])[0]"
              />

              <TextFormGroup
                  v-model="data.website"
                  name="website"
                  label="Website"
                  :error="(errors.website || [])[0]"
              />

              <TextFormGroup
                  v-model="data.address"
                  name="address"
                  label="Address"
                  :error="(errors.address || [])[0]"
              />

              <TextFormGroup
                  v-model="data.zip_code"
                  name="zip_code"
                  label="Zip Code"
                  :error="(errors.zip_code || [])[0]"
              />

              <TextFormGroup
                  v-model="data.city"
                  name="city"
                  label="City"
                  :error="(errors.city || [])[0]"
              />

              <SelectFormGroup
                  v-model="data.country"
                  name="country"
                  label="Country"
                  :error="(errors.country || [])[0]"
                  :options="countryOptions"
                  :nullable="true"
              />

              <TextFormGroup
                  v-model="data.facebook_url"
                  name="facebook_url"
                  label="Facebook URL"
                  :error="(errors.facebook_url || [])[0]"
              />

              <TextFormGroup
                  v-model="data.linkedin_url"
                  name="linkedin_url"
                  label="LinkedIn URL"
                  :error="(errors.linkedin_url || [])[0]"
              />

              <TextFormGroup
                  v-model="data.twitter_url"
                  name="twitter_url"
                  label="Twitter URL"
                  :error="(errors.twitter_url || [])[0]"
              />

              <TextFormGroup
                  v-model="data.instagram_url"
                  name="instagram_url"
                  label="Instagram URL"
                  :error="(errors.instagram_url || [])[0]"
              />

              <TextFormGroup
                  v-model="data.pinterest_url"
                  name="pinterest_url"
                  label="Pinterest URL"
                  :error="(errors.pinterest_url || [])[0]"
              />

              <RadioFormGroup
                  v-model="data.is_editorial"
                  name="is_editorial"
                  label="Is Editorial"
                  :error="(errors.is_editorial || [])[0]"
                  :options="[{value: true, label: 'Yes'}, {value: false, label: 'No'}]"
                  help-text="A flag indicating if this portal company is the editorial portal company."
              />

              <GalleryFormGroup
                  v-model="data.image_id"
                  name="image_id"
                  label="Image"
                  :error="(errors.image_id || [])[0]"
              />

              <GalleryFormGroup
                  v-model="data.logo_image_id"
                  name="logo_image_id"
                  label="Logo Image"
                  :error="(errors.logo_image_id || [])[0]"
              />

              <TypeaheadFormGroup
                  v-model="data.user_id"
                  name="user_id"
                  label="User"
                  endpoint="/api/v2/users"
                  :token="shared.token"
                  path="data.data"
                  placeholder="Search..."
                  :result-label-property="row => row.attributes.full_name"
              />

              <hr>

              <SubmitFormGroup button-class="btn-black"/>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
  </div>
</template>

<script>
import http from '@/http'
import notify from '@/notify'
import store from '@/store'
import CkEditorFormGroup from '@/vendor/forms/FormGroups/Horizontal/CkEditor'
import Errors from '@/mixins/Errors'
import GalleryFormGroup from '@/components/FormGroups/Horizontal/Gallery'
import HelpText from '@/components/HelpText'
import LoadingContainer from '@/components/Layout/LoadingContainer'
import RadioFormGroup from '@/vendor/forms/FormGroups/Horizontal/Radio'
import SelectFormGroup from '@/vendor/forms/FormGroups/Horizontal/Select'
import SubmitFormGroup from '@/vendor/forms/FormGroups/Horizontal/Submit'
import TextFormGroup from '@/vendor/forms/FormGroups/Horizontal/Text'
import TypeaheadFormGroup from "../../../../../vendor/forms/FormGroups/Horizontal/Typeahead.vue";

export default {
  components: {
    TypeaheadFormGroup,
    CkEditorFormGroup,
    GalleryFormGroup,
    HelpText,
    LoadingContainer,
    RadioFormGroup,
    SelectFormGroup,
    SubmitFormGroup,
    TextFormGroup,
  },

  mixins: [Errors],

  model: {
    prop: 'company',
    event: 'company-updated'
  },

  props: {
    company: {
      required: true
    }
  },

  data() {
    return {
      data: {},
      shared: store,

      portals: null,
      countries: null,

      loading: true
    }
  },
  mounted() {
    this.load()
  },

  computed: {
    portalOptions() {
      return this.portals.map(portal => {
        return {
          value: portal.id,
          label: portal.attributes.name
        }
      })
    },

    countryOptions() {
      return this.countries.map(country => {
        return {
          value: country.id,
          label: country.attributes.name
        }
      })
    }
  },

  methods: {
    load() {
      Promise.all([
        http.get('/api/v2/portals'),
        http.get('/api/v2/countries'),
      ]).then(responses => {
        this.portals = responses[0].data.data
        this.countries = responses[1].data.data

        this.loading = false
      })
    },

    submit() {
      http.post('/api/v2/portal-companies', {...this.data, company_id: this.company.data.id})
          .then(() => {
            notify.success('The portal company was saved')

            this.$router.push('/companies/' + this.company.data.id + '/portals')
          })
          .catch(this.catchValidationErrors)
    }
  }
}
</script>
